// ADR-133: pure helpers for Reports → Ingestion Sync Runs.
import {
  INGESTION_SYNC_RUN_VIEWS,
  GENERIC_COLUMN_EXCLUDED_KEYS,
  GENERIC_COLUMN_LIMIT,
} from '../constants/ingestionSyncRunViews';
import { SYNC_RUN_ORIGIN_LABELS } from '../constants/ingestionSyncRuns';

/** 734512 → "12m 14s"; null → "—". */
export const formatDuration = (ms) => {
  if (ms === null || ms === undefined || Number.isNaN(Number(ms))) {
    return '—';
  }
  const totalSeconds = Math.max(0, Math.round(Number(ms) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

/** "ftRenewalCoalesced" → "Ft renewal coalesced"; "_meta" → "Run metadata". */
export const formatSectionLabel = (sectionKey) => {
  if (!sectionKey) {
    return '';
  }
  if (sectionKey === '_meta') {
    return 'Run metadata';
  }
  const words = String(sectionKey)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const formatCellValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
};

/**
 * The extra columns for one section: its descriptor when one matches the run's
 * result schema version, otherwise the first scalar fields seen in the preview.
 */
export const resolveSectionColumns = ({ provider, syncType, sectionKey, resultSchemaVersion = 1, items = [] }) => {
  const descriptor = INGESTION_SYNC_RUN_VIEWS[`${provider}:${syncType}:${sectionKey}`];
  if (descriptor
    && (descriptor.fromVersion === undefined || resultSchemaVersion >= descriptor.fromVersion)
    && (descriptor.toVersion === undefined || resultSchemaVersion <= descriptor.toVersion)) {
    return descriptor.columns;
  }
  const keys = [];
  for (const item of items) {
    for (const key of Object.keys(item?.preview || {})) {
      if (!GENERIC_COLUMN_EXCLUDED_KEYS.has(key) && !keys.includes(key)) {
        keys.push(key);
      }
    }
    if (keys.length >= GENERIC_COLUMN_LIMIT) {
      break;
    }
  }
  return keys.slice(0, GENERIC_COLUMN_LIMIT).map((key) => ({ path: key, label: formatSectionLabel(key) }));
};

/** "Step 3/4" for a run inside a batch, "Standalone" otherwise. */
export const describeBatchPosition = (run) => {
  if (!run?.batchId) {
    return 'Standalone';
  }
  if (run.sequence && run.batchStepCount) {
    return `Step ${run.sequence}/${run.batchStepCount}`;
  }
  return 'In batch';
};

/** "radar-api v17" / "code" / "—" for the mappings column. */
export const describeMappingSet = (run) => {
  if (!run?.jobPositionMappingSource) {
    return '—';
  }
  if (run.jobPositionMappingSource === 'radar-api' && run.jobPositionMappingSetVersion !== null && run.jobPositionMappingSetVersion !== undefined) {
    return `radar-api v${run.jobPositionMappingSetVersion}`;
  }
  return run.jobPositionMappingSource;
};

const sumOf = (runs, field) => runs.reduce((total, run) => total + (Number(run[field]) || 0), 0);

const batchStatusOf = (runs, batchStatus) => {
  if (batchStatus) {
    return batchStatus;
  }
  if (runs.some((run) => run.status === 'failed')) {
    return 'failed';
  }
  return runs.some((run) => run.status === 'running') ? 'running' : 'success';
};

/**
 * Rows for the runs list: a multi-step batch (the Americas pipeline) becomes a
 * parent row with its runs as children in step order; standalone runs and
 * one-step batches (PowerBI) stay plain rows. Groups keep the position of
 * their newest run, and runs of a batch split across pages join their group.
 */
export const groupRunsByBatch = (runs = []) => {
  const rows = [];
  const groups = new Map();
  for (const run of runs) {
    const isMultiStep = Boolean(run.batchId) && (run.batchStepCount || 0) > 1;
    if (!isMultiStep) {
      rows.push({ type: 'run', key: run.id, run });
      continue;
    }
    let group = groups.get(run.batchId);
    if (group && !group.batchStatus && run.batchStatus) {
      group.batchStatus = run.batchStatus;
    }
    if (!group) {
      group = { type: 'batch', key: `batch:${run.batchId}`, batchId: run.batchId, batchStatus: run.batchStatus || null, runs: [] };
      groups.set(run.batchId, group);
      rows.push(group);
    }
    group.runs.push(run);
  }

  for (const group of groups.values()) {
    group.runs.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    const { runs: children } = group;
    const starts = children.map((run) => new Date(run.startedAt).getTime()).filter(Number.isFinite);
    const finishes = children.map((run) => (run.finishedAt ? new Date(run.finishedAt).getTime() : NaN));
    const allFinished = finishes.every(Number.isFinite);
    const startedAt = starts.length ? new Date(Math.min(...starts)).toISOString() : null;
    group.provider = children[0].provider;
    group.mode = children[0].mode;
    group.trigger = children[0].trigger;
    group.triggeredByUser = children.find((run) => run.triggeredByUser)?.triggeredByUser || null;
    group.stepCount = children[0].batchStepCount || children.length;
    group.status = batchStatusOf(children, group.batchStatus);
    group.startedAt = startedAt;
    group.durationMs = allFinished && startedAt ? Math.max(...finishes) - Math.min(...starts) : null;
    group.createdCount = sumOf(children, 'createdCount');
    group.updatedCount = sumOf(children, 'updatedCount');
    group.skippedCount = sumOf(children, 'skippedCount');
    group.errorCount = sumOf(children, 'errorCount');
    group.hasIncompleteResult = children.some((run) => run.resultStatus === 'INCOMPLETE');
    group.mappingRun = children.find((run) => run.jobPositionMappingSource) || null;
  }
  return rows;
};

/**
 * "Scheduled" / "Manual · ops@x.com" / "Manual" / "Direct API call", plus the
 * internal job that started it on the user's behalf: "Manual · ops@x.com · Bench candidate import".
 */
export const describeTrigger = ({ trigger, triggeredByUser, origin } = {}) => {
  const labels = { SCHEDULER: 'Scheduled', MANUAL: 'Manual', API: 'Direct API call' };
  const parts = [labels[trigger] || trigger || '—'];
  if (trigger === 'MANUAL' && triggeredByUser) {
    parts.push(triggeredByUser);
  }
  if (trigger === 'MANUAL' && origin) {
    parts.push(SYNC_RUN_ORIGIN_LABELS[origin] || origin);
  }
  return parts.join(' · ');
};

export const sectionOptionLabel = (section) => {
  const count = section.itemCount ?? 0;
  const base = `${formatSectionLabel(section.sectionKey)} · ${count.toLocaleString('en-US')}`;
  if (section.hidden) {
    return `${base} · not displayed`;
  }
  if (section.declaredItemCount !== undefined && count < section.declaredItemCount) {
    return `${base} of ${section.declaredItemCount.toLocaleString('en-US')} · incomplete`;
  }
  return base;
};
